import { useState, useEffect } from "react";
import { Trash2, FolderInput, Tag as TagIcon, X, CheckSquare, Loader2 } from "lucide-react";
import api from "@/lib/api";
import { TID } from "@/constants/testIds";

/**
 * Bottom-anchored action bar that appears when user has selected one or more assets
 * in "select mode". Calls POST /api/assets/bulk for delete/move/tag-add.
 */
export default function BulkActionBar({ selectedIds, totalCount, allFolders, currentFolderId, onSelectAll, onClear, onDone }) {
  const [busy, setBusy] = useState(false);
  const [showMove, setShowMove] = useState(false);
  const [showTag, setShowTag] = useState(false);
  const [tagMode, setTagMode] = useState("add"); // "add" | "remove"
  const [tagInput, setTagInput] = useState("");
  const [suggestions, setSuggestions] = useState([]);
  const count = selectedIds.length;

  // Fetch the recursive tag pool for autocomplete when the tag popover opens.
  // Uses /tags/suggestions (project subtree) so power tagging works even when
  // the selected videos live in a fresh subfolder. Grid filter chips stay
  // strict via the untouched /tags endpoint.
  useEffect(() => {
    if (!showTag) return;
    const path = currentFolderId ? "/tags/suggestions" : "/tags";
    const params = currentFolderId ? { folder_id: currentFolderId } : {};
    api.get(path, { params }).then((r) => setSuggestions(r.data)).catch(() => setSuggestions([]));
  }, [showTag, currentFolderId]);

  // Split "promo, hero, sh" → last token = "sh" so we filter suggestions on
  // just the tag the user is currently typing. Clicking a suggestion replaces
  // that trailing token and appends ", " for the next tag.
  const currentToken = tagInput.split(",").pop().trim().toLowerCase();
  const already = new Set(
    tagInput.split(",").map((t) => t.trim().toLowerCase()).filter(Boolean)
  );
  const filteredSuggestions = suggestions
    .filter((t) => !already.has(t.name) && (currentToken === "" || t.name.toLowerCase().includes(currentToken)))
    .slice(0, 8);

  const applySuggestion = (name) => {
    const parts = tagInput.split(",");
    parts[parts.length - 1] = ` ${name}`;
    setTagInput(parts.join(",").replace(/^\s+/, "") + ", ");
  };

  const run = async (action, extra = {}) => {
    setBusy(true);
    try {
      await api.post("/assets/bulk", { action, asset_ids: selectedIds, ...extra });
      onDone?.();
    } catch (e) {
      alert(e.response?.data?.detail || "Bulk action failed");
    } finally {
      setBusy(false);
      setShowMove(false);
      setShowTag(false);
      setTagInput("");
    }
  };

  const handleDelete = async () => {
    if (!window.confirm(`Delete ${count} video${count === 1 ? "" : "s"}? This removes them from Bunny too.`)) return;
    await run("delete");
  };

  const handleMove = async (folderId) => {
    if (folderId === currentFolderId) { setShowMove(false); return; }
    await run("move", { folder_id: folderId });
  };

  const handleApplyTags = async () => {
    const tags = tagInput.split(",").map((t) => t.trim()).filter(Boolean);
    if (tags.length === 0) return;
    await run(tagMode === "add" ? "tag-add" : "tag-remove", { tags });
  };

  return (
    <div
      data-testid={TID.bulkActionBar}
      className="fixed bottom-6 left-1/2 -translate-x-1/2 z-40 bg-zinc-950/95 backdrop-blur-xl border border-zinc-800 rounded-full shadow-[0_8px_40px_rgba(0,0,0,0.6)] flex items-center gap-1 px-2 py-1.5"
    >
      <div className="flex items-center gap-2 px-3 text-xs">
        <CheckSquare className="w-3.5 h-3.5 text-[var(--brand-accent)]" />
        <span className="text-zinc-200 font-medium">{count}</span>
        <span className="text-zinc-500">selected</span>
      </div>

      <div className="w-px h-6 bg-zinc-800" />

      <button
        data-testid={TID.bulkSelectAll}
        onClick={onSelectAll}
        disabled={count === totalCount}
        className="text-[11px] uppercase tracking-wider text-zinc-400 hover:text-zinc-200 px-3 py-1.5 transition disabled:opacity-40"
      >
        Select all ({totalCount})
      </button>

      <div className="w-px h-6 bg-zinc-800" />

      <div className="relative">
        <button
          data-testid={TID.bulkMoveBtn}
          onClick={() => { setShowMove((p) => !p); setShowTag(false); }}
          disabled={busy}
          className="flex items-center gap-1.5 text-xs text-zinc-300 hover:text-[var(--brand-accent)] px-3 py-1.5 rounded-full transition"
        >
          <FolderInput className="w-3.5 h-3.5" /> Move
        </button>
        {showMove && (
          <div className="absolute bottom-full mb-2 left-0 w-64 max-h-64 overflow-y-auto bg-zinc-950 border border-zinc-800 rounded-md p-1 shadow-2xl">
            {allFolders.length === 0 ? (
              <div className="text-[10px] text-zinc-500 px-2 py-3 text-center">No folders</div>
            ) : (
              allFolders.map((f) => (
                <button
                  key={f.id}
                  onClick={() => handleMove(f.id)}
                  disabled={f.id === currentFolderId}
                  className="w-full text-left text-xs text-zinc-300 hover:bg-zinc-900 px-2 py-1.5 rounded disabled:opacity-40 disabled:cursor-not-allowed"
                >
                  {f.name}
                  {f.id === currentFolderId && <span className="text-[9px] text-zinc-600 ml-2">current</span>}
                </button>
              ))
            )}
          </div>
        )}
      </div>

      <div className="relative">
        <button
          data-testid={TID.bulkTagBtn}
          onClick={() => { setShowTag((p) => !p); setShowMove(false); }}
          disabled={busy}
          className="flex items-center gap-1.5 text-xs text-zinc-300 hover:text-[var(--brand-accent)] px-3 py-1.5 rounded-full transition"
        >
          <TagIcon className="w-3.5 h-3.5" /> Tag
        </button>
        {showTag && (
          <div className="absolute bottom-full mb-2 left-0 w-72 bg-zinc-950 border border-zinc-800 rounded-md p-3 shadow-2xl">
            <div className="flex gap-1 mb-2 bg-black/40 rounded-md p-0.5">
              <button
                data-testid="bulk-tag-tab-add"
                onClick={() => setTagMode("add")}
                className={`flex-1 text-[10px] uppercase tracking-wider py-1 rounded transition ${tagMode === "add" ? "bg-[var(--brand-accent)] text-black font-semibold" : "text-zinc-400 hover:text-zinc-200"}`}
              >
                Add
              </button>
              <button
                data-testid="bulk-tag-tab-remove"
                onClick={() => setTagMode("remove")}
                className={`flex-1 text-[10px] uppercase tracking-wider py-1 rounded transition ${tagMode === "remove" ? "bg-red-500 text-black font-semibold" : "text-zinc-400 hover:text-zinc-200"}`}
              >
                Remove
              </button>
            </div>
            <div className="text-[10px] uppercase tracking-wider text-zinc-500 mb-2">
              {tagMode === "add" ? "Add tags (comma separated)" : "Remove tags (comma separated)"}
            </div>
            <input
              autoFocus
              data-testid="bulk-tag-input"
              value={tagInput}
              onChange={(e) => setTagInput(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === "Enter" && filteredSuggestions.length === 0) handleApplyTags();
                if (e.key === "Enter" && filteredSuggestions.length > 0 && currentToken) {
                  e.preventDefault();
                  applySuggestion(filteredSuggestions[0].name);
                }
              }}
              placeholder={tagMode === "add" ? "e.g. promo, 2026, hero" : "e.g. draft, wip"}
              className="w-full bg-black/40 border border-zinc-800 rounded px-3 py-2 text-sm focus:outline-none focus:border-[var(--brand-accent)]/60 mb-2"
            />
            {filteredSuggestions.length > 0 && (
              <div
                data-testid="bulk-tag-suggestions"
                className="mb-2 flex flex-wrap gap-1 max-h-24 overflow-y-auto"
              >
                {filteredSuggestions.map((t) => (
                  <button
                    key={t.name}
                    type="button"
                    data-testid={`bulk-tag-suggestion-${t.name}`}
                    onClick={() => applySuggestion(t.name)}
                    className="text-[10px] uppercase tracking-wider text-zinc-300 bg-zinc-900/60 hover:bg-[var(--brand-accent)]/20 hover:text-[var(--brand-accent)] border border-zinc-800 hover:border-[var(--brand-accent)]/40 px-2 py-1 rounded-full transition"
                    title={`Used on ${t.count} ${t.count === 1 ? "video" : "videos"} in this project`}
                  >
                    {t.name}
                    <span className="ml-1 opacity-60">{t.count}</span>
                  </button>
                ))}
              </div>
            )}
            <button
              data-testid="bulk-tag-apply"
              onClick={handleApplyTags}
              disabled={busy || !tagInput.trim()}
              className={`w-full font-semibold rounded-md py-1.5 text-xs transition disabled:opacity-60 ${tagMode === "add" ? "bg-[var(--brand-accent)] text-black hover:bg-[var(--brand-accent)]/90" : "bg-red-500 text-black hover:bg-red-500/90"}`}
            >
              {tagMode === "add" ? "Apply tags" : "Remove tags"}
            </button>
          </div>
        )}
      </div>

      <button
        data-testid={TID.bulkDeleteBtn}
        onClick={handleDelete}
        disabled={busy}
        className="flex items-center gap-1.5 text-xs text-red-400 hover:text-red-300 px-3 py-1.5 rounded-full transition"
      >
        {busy ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Trash2 className="w-3.5 h-3.5" />} Delete
      </button>

      <div className="w-px h-6 bg-zinc-800" />

      <button
        data-testid={TID.bulkClear}
        onClick={onClear}
        className="text-zinc-500 hover:text-zinc-200 px-2 py-1.5 transition"
        title="Exit selection mode"
      >
        <X className="w-3.5 h-3.5" />
      </button>
    </div>
  );
}
